/**
 * Diagram Modal - CSS-based fullscreen without moving elements
 * This approach keeps the diagram in place and uses CSS to make it fullscreen
 */

import ParentMessenger, { MessageTypes } from './parent-messenger.js';

export class DiagramModal {
  private diagramContainer: HTMLElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private isOpen: boolean = false;
  private closeResolver: ((value: void) => void) | null = null;
  private originalStyles: { [key: string]: string } = {};

  constructor() {
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
  }

  /**
   * Initialize the modal (just adds styles)
   */
  initialize(): void {
    // Add styles
    this.addStyles();
  }

  /**
   * Add modal styles
   */
  private addStyles(): void {
    if (document.getElementById('diagram-modal-styles')) return;

    const style = document.createElement('style');
    style.id = 'diagram-modal-styles';
    style.textContent = `

      /* Diagram in fullscreen mode */
      .diagram-container.fullscreen-mode {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-height: 100% !important;
        z-index: 10000 !important;
        background-color: white !important;
        margin: 0 !important;
        border-radius: 0 !important;
        overflow: hidden !important;
      }

      /* Ensure wrapper fills the space */
      .fullscreen-mode #diagramWrapper {
        height: 100% !important;
      }

      /* Show controls in fullscreen */
      .fullscreen-mode .diagram-controls {
        display: flex !important;
      }

      /* Show overlay elements in fullscreen */
      .fullscreen-mode #partialReportOverlay,
      .fullscreen-mode .diagram-disclaimer {
        display: block !important;
      }

      /* Hide preview-specific elements in fullscreen */
      .fullscreen-mode .diagram-preview-overlay {
        display: none !important;
      }

      /* Hide the view fullscreen button specifically */
      .fullscreen-mode > button.brand-button-compact {
        display: none !important;
      }

      /* Close button */
      .diagram-close-button {
        position: fixed;
        top: 16px;
        right: 16px;
        z-index: 10001;
        background-color: white;
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        transition: background-color 0.2s, box-shadow 0.2s;
      }

      .diagram-close-button:hover {
        background-color: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      /* Ensure body doesn't scroll when modal is open */
      body.modal-open {
        overflow: hidden !important;
      }

      /* Fix z-index stacking context issues in marketing embed */
      body.modal-open #embedPageContainer,
      body.modal-open #resultsContainer {
        z-index: auto !important;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Open the modal (just adds classes, no element moving)
   */
  async open(diagramContainer: HTMLElement): Promise<void> {
    if (this.isOpen) return;

    this.diagramContainer = diagramContainer;

    // Store original styles we'll override
    this.originalStyles = {
      position: diagramContainer.style.position || '',
      top: diagramContainer.style.top || '',
      left: diagramContainer.style.left || '',
      right: diagramContainer.style.right || '',
      bottom: diagramContainer.style.bottom || '',
      width: diagramContainer.style.width || '',
      height: diagramContainer.style.height || '',
      zIndex: diagramContainer.style.zIndex || ''
    };

    // Create close button
    this.closeButton = document.createElement('button');
    this.closeButton.className = 'diagram-close-button';
    this.closeButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
    this.closeButton.addEventListener('click', this.close);
    document.body.appendChild(this.closeButton);

    // Request parent to make iframe fullscreen (if we're in an iframe)
    if (window.parent !== window) {
      ParentMessenger.send(MessageTypes.FULLSCREEN_REQUEST, { action: 'enter' });
    }

    // Add fullscreen class to diagram
    diagramContainer.classList.add('fullscreen-mode');

    // Prevent body scroll
    document.body.classList.add('modal-open');

    // Set open state
    this.isOpen = true;

    // Recenter panzoom for fullscreen viewport after layout is painted.
    // Double rAF guarantees the CSS has been committed and painted.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.mermaidDiagramManager?.recenterForViewport?.();
      });
    });

    // Add keyboard listener
    document.addEventListener('keydown', this.handleEscape);

    // Return promise that resolves when modal is closed
    return new Promise<void>((resolve) => {
      this.closeResolver = resolve;
    });
  }

  /**
   * Close the modal (just removes classes)
   */
  async close(): Promise<void> {
    if (!this.isOpen || !this.diagramContainer) return;

    // Remove fullscreen class
    this.diagramContainer.classList.remove('fullscreen-mode');

    // Restore original styles
    Object.assign(this.diagramContainer.style, this.originalStyles);

    // Remove close button
    this.closeButton?.remove();
    this.closeButton = null;

    // Restore body scroll
    document.body.classList.remove('modal-open');

    // Set closed state
    this.isOpen = false;

    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleEscape);

    // Request parent to exit fullscreen (if we're in an iframe)
    if (window.parent !== window) {
      ParentMessenger.send(MessageTypes.FULLSCREEN_REQUEST, { action: 'exit' });
    }

    // Restore panzoom's original centering and reset to default view
    window.mermaidDiagramManager?.restoreOriginalCenter?.();
    window.mermaidDiagramManager?.zoomReset?.();

    // Clear reference
    this.diagramContainer = null;

    // Resolve the close promise
    if (this.closeResolver) {
      this.closeResolver();
      this.closeResolver = null;
    }
  }

  /**
   * Handle escape key
   */
  private handleEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

}

// Create and export singleton instance
export const diagramModal = new DiagramModal();

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => diagramModal.initialize());
} else {
  diagramModal.initialize();
}
